// components/ProductCard/ProductCard.js
import React, { useState } from 'react';
import './ProductCard.css';

const ProductCard = ({ 
  product, 
  onProductClick, 
  cartQuantity = 0,
  isSelected = false,
  onToggleCompare = null
}) => {
  const { title, description, image, price, originalPrice, features } = product;
  
  // Lazy loading state
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Always in stock
  const inStock = true;

  const discountPercentage = originalPrice ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;

  const handleViewDetails = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (onProductClick && typeof onProductClick === 'function') {
      onProductClick();
    }
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return `$ ${price.toLocaleString()}`;
    }
    return price;
  };

  return (
    <div 
      className={`product-card fade-in ${isSelected ? 'selected-for-compare' : ''}`} 
      style={isSelected ? { border: '2px solid #2196F3' } : {}}
      itemScope 
      itemType="https://schema.org/Product"
    >
      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={description || `${title} - High quality security camera equipment by Transcontinental Network`} />
      <meta itemProp="image" content={image} />
      <div itemProp="offers" itemScope itemType="https://schema.org/Offer" style={{ display: 'none' }}>
        <meta itemProp="price" content={price} />
        <meta itemProp="priceCurrency" content="USD" />
        <meta itemProp="availability" content="https://schema.org/InStock" />
      </div>

      {/* Product Image */}
      <div className="product-image" onClick={handleViewDetails}>
        
        {/* Compare Checkbox */}
        {onToggleCompare && (
          <div 
            className="compare-checkbox-container" 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              padding: '6px 10px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
              cursor: 'pointer',
              border: isSelected ? '1px solid #2196F3' : '1px solid transparent'
            }}
          >
            <input 
              type="checkbox" 
              checked={isSelected} 
              onChange={() => onToggleCompare(product)}
              style={{ cursor: 'pointer', width: '16px', height: '16px', margin: 0 }}
            />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#333' }}>Compare</span>
          </div>
        )}

        {image ? (
          <div style={{ position: 'relative', width: '100%', height: '250px', overflow: 'hidden' }}>
            {/* Loading Placeholder */}
            {!imageLoaded && !imageError && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: '1rem',
                zIndex: 1
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid #e2e8f0',
                  borderTop: '2px solid #0d9488',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              </div>
            )}
            
            {/* Actual Image */}
            {!imageError && (
              <img 
                src={image} 
                alt={title ? `${title} - CCTV Security Camera Equipment` : 'Transcontinental Network Security Product'}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={() => {
                  setImageError(true);
                  setImageLoaded(true);
                }}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                  position: 'relative',
                  zIndex: 2,
                  margin: 'auto'
                }}
              />
            )}
            
            {/* Error Placeholder */}
            {imageError && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#94a3b8',
                zIndex: 3
              }}>
                <svg viewBox="0 0 24 24" width="40" height="40" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}>
                  <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
                  <polygon points="12 22.08 12 12 3 6.92 3 17.08 12 22.08" />
                  <polygon points="12 22.08 21 17.08 21 6.92 12 12 12 22.08" />
                  <polygon points="12 12 21 6.92 12 1.84 3 6.92 12 12" />
                </svg>
              </div>
            )}
          </div>
        ) : (
          <div className="no-image-placeholder" style={{
            width: '100%',
            height: '250px',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8'
          }}>
            <svg viewBox="0 0 24 24" width="40" height="40" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}>
              <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
              <polygon points="12 22.08 12 12 3 6.92 3 17.08 12 22.08" />
              <polygon points="12 22.08 21 17.08 21 6.92 12 12 12 22.08" />
              <polygon points="12 12 21 6.92 12 1.84 3 6.92 12 12" />
            </svg>
          </div>
        )}
        
        <div className="product-badges">
          {discountPercentage > 0 && (
            <span className="discount-badge">-{discountPercentage}%</span>
          )}
          {product.is_featured && (
            <span className="featured-badge" style={{
              position: 'absolute',
              top: onToggleCompare ? '50px' : '10px',
              left: '10px',
              backgroundColor: '#ff9800',
              color: 'white',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              zIndex: 5
            }}>
              Featured
            </span>
          )}
        </div>
        
        {/* Cart Badge */}
        {cartQuantity > 0 && (
          <div className="cart-badge" style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: '#4caf50',
            color: 'white',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: 'bold',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            zIndex: 5
          }}>
            {cartQuantity} in cart
          </div>
        )}
        
        <div className="product-actions">
          <button 
            className="quick-view-btn"
            onClick={handleViewDetails}
            title="Quick View"
          >
            👁️
          </button>
        </div>
      </div>

      {/* Product Info */}
      <div className="product-info">
        <div className="product-header">
          <h3 className="product-title" onClick={handleViewDetails} style={{ cursor: 'pointer' }}>
            {title}
          </h3>
        </div>

        <p className="product-description">{description}</p>

        {/* Product Features */}
        {features && features.length > 0 && (
          <div className="product-features">
            <h4 className="features-title">Features:</h4>
            <ul className="features-list">
              {features.slice(0, 2).map((feature, index) => (
                <li key={index} className="feature-item">
                  <span className="feature-bullet">•</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pricing */}
        <div className="product-pricing">
          <div className="price-wrapper">
            <span className="current-price">{formatPrice(price)}</span>
            {originalPrice && (
              <span className="original-price">{formatPrice(originalPrice)}</span>
            )}
          </div>
        </div>

        {/* View Details Button */}
        <div className="product-details">
          <button 
            className="btn btn-primary view-details-btn"
            onClick={handleViewDetails}
            style={{ width: '100%', marginTop: '1rem' }}
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;